import type {
  ReviewCard,
  ReviewCardChange,
  ReviewCardItem,
  ReviewCardTotals,
} from '@/types/gtm-agents';

/**
 * A cost the card shows as the coin and a number (CardCost), never the word:
 * the amount and what it is per.
 */
export interface ReviewCardCost {
  amount: number;
  unit: 'record' | 'run' | 'week' | 'month';
}

const hasNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const numberOrNull = (value: unknown): number | null =>
  hasNumber(value) ? value : null;

/**
 * What a step on the card costs at most: per run for a brief, per record
 * otherwise. Free for a step that only reaches outside the account. Null
 * when its changes say it all (error handling, the run limits) or its price
 * isn't known.
 */
export function reviewCardItemCost(
  item: ReviewCardItem
): ReviewCardCost | 'free' | null {
  if (item.reason === 'errors' || item.type === 'limits') return null;
  if (hasNumber(item.credits) && item.credits > 0)
    return {
      amount: item.credits,
      unit: item.type === 'agent_task' ? 'run' : 'record',
    };
  return item.reason === 'outside' ? 'free' : null;
}

/** "1,250", "2.5". */
function amount(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString('en-US', { maximumFractionDigits: 2 })
    : '?';
}

/**
 * One thing an item raises against the saved play. A credit change carries
 * its amounts for the coin: "Credit limit" with 2,000 now and 1,000 before,
 * "No credit limit" with none now. Null for a number not known.
 */
export interface ReviewCardChangeLine {
  text: string;
  credits?: { now: number | null; was: number | null };
}

/** One thing an item raises, as the card names it. */
function changeLine(change: ReviewCardChange): ReviewCardChangeLine | null {
  const { was, now } = change;
  const line = (text: string) => ({ text });
  switch (change.key) {
    case 'perPeriod':
      return line(
        !change.per || change.per === 'run'
          ? `Up to ${amount(now)} a run, was ${amount(was)}`
          : `Runs up to ${amount(now)} a ${change.per}, was ${amount(was)}`
      );
    case 'schedule':
      return line(
        typeof now === 'string' && typeof was === 'string'
          ? `Runs ${now}, was ${was}`
          : 'Runs more often'
      );
    case 'scope':
      return line(
        now === 'all_rows'
          ? 'Reads all rows, was new rows only'
          : 'Reads all matches, was new matches only'
      );
    case 'backfill':
      return line('Reads past records too');
    case 'maxPeoplePerAccount':
      return line(`Up to ${amount(now)} people a company, was ${amount(was)}`);
    case 'skipInCrm':
      return line(
        now && typeof now === 'object' && 'enabled' in now && now.enabled
          ? 'Skips fewer people already in your CRM'
          : 'No longer skips people already in your CRM'
      );
    case 'recordsPerRun':
      return line(
        hasNumber(now)
          ? `Up to ${amount(now)} records a run, was ${amount(was)}`
          : `No record limit, was ${amount(was)}`
      );
    case 'creditsPerRun':
      return {
        text: hasNumber(now) ? 'Credit limit' : 'No credit limit',
        credits: { now: numberOrNull(now), was: numberOrNull(was) },
      };
    case 'modelCreditsPerRun':
      return {
        text: 'Brief budget',
        credits: { now: numberOrNull(now), was: numberOrNull(was) },
      };
    case 'onError':
      return line('Error handling changed');
    case 'retries':
      return line(
        `Retries failed records ${amount(now)} times, was ${amount(was)}`
      );
    case 'retryUnconfirmed':
      return line('Can resend a post that may have gone out');
    case 'writeSafety':
      return line('Writes to owned records');
    case 'assignment':
      if (Array.isArray(change.fields) && change.fields.length) {
        const fields = change.fields.join(', ');
        return line(
          was == null
            ? `Fills ${fields} by round robin`
            : `Round robin users changed for ${fields}`
        );
      }
      return line(was == null ? 'Assigns owners' : 'Owner assignment changed');
    case 'writeMode':
      return line('Overwrites fields');
    case 'syncProfileId':
      return line('Sync profile changed');
    default:
      return null;
  }
}

/** Each thing an item raises against the saved play, one line apiece. */
export function reviewCardChanges(
  item: ReviewCardItem
): ReviewCardChangeLine[] {
  return (Array.isArray(item.changes) ? item.changes : [])
    .map((change) =>
      change && typeof change === 'object' ? changeLine(change) : null
    )
    .filter((line): line is ReviewCardChangeLine => Boolean(line));
}

/**
 * The most a run and a week charge, as costs: a play that runs once a month
 * by its month (once a quarter, by its run). Empty when neither is known.
 * The last is the period total a card's header shows.
 */
export function reviewCardTotalCosts(
  totals: ReviewCardTotals | null | undefined
): ReviewCardCost[] {
  if (!totals) return [];
  const { perRunMax, perWeekMax, period } = totals;
  if (period && hasNumber(perRunMax))
    return [{ amount: perRunMax, unit: period === 'month' ? 'month' : 'run' }];
  const costs: ReviewCardCost[] = [];
  if (hasNumber(perRunMax)) costs.push({ amount: perRunMax, unit: 'run' });
  if (hasNumber(perWeekMax)) costs.push({ amount: perWeekMax, unit: 'week' });
  return costs;
}

/**
 * The canvas strip's words for the totals (embedded-bar), until it shows
 * them as costs: the most a run and a week charge, in one line; null when
 * unknown. Clu's card uses reviewCardTotalCosts.
 */
export function reviewCardTotals(
  totals: ReviewCardTotals | null | undefined
): string | null {
  if (!totals) return null;
  const { perRunMax, perWeekMax, period } = totals;
  if (period && hasNumber(perRunMax))
    return `Up to ${amount(perRunMax)} credits a ${period}`;
  if (hasNumber(perRunMax) && hasNumber(perWeekMax))
    return `Up to ${amount(perRunMax)} credits a run, up to ${amount(perWeekMax)} a week`;
  if (hasNumber(perRunMax)) return `Up to ${amount(perRunMax)} credits a run`;
  if (hasNumber(perWeekMax))
    return `Up to ${amount(perWeekMax)} credits a week`;
  return null;
}

/** The card in one line, for the canvas's proposal strip (embedded-bar). */
export function reviewCardLine(card: ReviewCard): string {
  const count = card.items.length;
  return `Accept confirms ${count} ${count === 1 ? 'step' : 'steps'} that ${
    count === 1 ? 'spends' : 'spend'
  } credits or ${count === 1 ? 'reaches' : 'reach'} outside your account`;
}
